import { Link, useParams, useSearchParams } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import { useQuery } from '@tanstack/react-query';
import {
  Attachments,
  GetInvestigationByIdResponse,
  IncidentsStatusEnum,
  InvestigationAffectedBodyPartsEnum,
  InvestigationQuestionTypeEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
  LoginResponseData,
} from '@EHSNavigator/backend';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import DownloadIcon from '../../assets/icons/download-icon.svg?react';
import Loader from '../../components/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import { toggleModal } from '../../reducers/modalToggle';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import Button from '../../components/Button';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import InjuryIcon from '../../assets/icons/injuries-icon.svg?react';
import { Fragment, useState } from 'react';
import clsx from 'clsx';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import ChangesHistory from '../../components/ChangesHistory';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { useLocalStorage } from '@uidotdev/usehooks';
import moment from 'moment';
import AttachmentPreviewModal from '../../components/AttachmentPreviewModal';
import ModalComponent from '../../components/ModalComponent';

export default function InjuriesDetailsPage() {
  const { InvestigationReportingApi } = useApiClients();
  const dispatch = useDispatch();
  const [toggle, setToggle] = useState('');
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachments | null>(null);
  const params = useParams();
  const id = params['injuries-view-by-id'] || '';
  const [userInfo] = useLocalStorage<LoginResponseData | null>('userInfo');
  const [searchParams] = useSearchParams();
  const onDemandEditParam = searchParams.get('onDemandEdit') ?? 'false';
  const onDemandEdit = onDemandEditParam === 'true';
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const { data = undefined, isLoading } = useQuery<
    GetInvestigationByIdResponse['investigation']
  >({
    queryKey: ['get-investigation-by-id', id],
    queryFn: async () => {
      const response =
        await InvestigationReportingApi.investigationReportingControllerGetInvestigationById(
          {
            investigationId: id,
          },
        );
      return response?.data?.investigation;
    },
  });
  const buttonSize = useButtonSizeLarge();

  if (isLoading) {
    return <Loader />;
  }

  const canEdit =
    onDemandEdit ||
    (data?.incident?.status !== IncidentsStatusEnum.Closed &&
      !data?.submittedToOsha &&
      (userInfo?.user?.role?.name === 'Owner' ||
        userInfo?.user?.id === data?.user?.id));

  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <div className="flex flex-row items-start gap-2.5 md:hidden">
        <button
          type="button"
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 w-fit cursor-pointer border p-0.5"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex flex-col gap-1">
          <span className="text-brand-byzantine font-inter-variable text-xs leading-4 font-bold">
            INVESTIGATION
          </span>
          <h4 className="text-neutral-tones-text font-inter-variable font-bold">
            Investigation Report
          </h4>
        </div>
      </div>
      <div className="hidden md:block">
        <div className="flex justify-between">
          <Button
            href="/injuries"
            variant="link"
            otherClasses="gap-2.5 w-fit mb-15"
          >
            <span className="bg-brand-byzantine rounded-5 flex h-8 w-8 items-center justify-center">
              <ChevronLeftIcon className="h-7 w-7 rotate-90 [&>path]:fill-white" />
            </span>
            <strong>Return</strong>
          </Button>
        </div>
        <h4 className="text-neutral-tones-text font-inter-variable mt-2.5 font-bold">
          Investigation Report
        </h4>
      </div>
      <div className="mt-15 flex flex-wrap gap-x-2 gap-y-4 md:gap-x-15">
        <p className="text-neutral-tones-text/80 font-inter-variable -tracking-form-label lg:-tracking-button flex items-center gap-1 text-sm leading-5 md:gap-2.5 lg:gap-15 lg:text-xl lg:leading-6">
          Investigation Reference
          <span className="text-neutral-tones-text/50 uppercase">
            {data?.investigationRefId}
          </span>
        </p>
        <p className="text-neutral-tones-text/80 font-inter-variable -tracking-form-label lg:-tracking-button sm:border-l-neutral-tones-text/10 flex items-center gap-2 text-sm leading-5 sm:border-l sm:pl-2 md:gap-2.5 md:pl-15 lg:gap-15 lg:text-xl lg:leading-6">
          Investigator
          <span className="text-brand-byzantine font-bold capitalize">
            {data?.user?.fullName}
          </span>
        </p>
      </div>
      <div className="border-y-neutral-tones-text/10 my-8 w-full border-y py-5 lg:my-9">
        <div className="bg-card-backgrounds/5 rounded-10 border-neutral-tones-text/10 flex w-full items-center gap-2.5 border-2 p-15">
          <InjuryIcon className="[&>path]:fill-brand-fair-orange h-12 w-12 md:h-9 md:w-9 lg:h-12 lg:w-12" />
          <div className="flex flex-1 flex-col gap-1 lg:gap-2.5">
            <span className="font-inter-variable text-brand-fair-orange -tracking-form-input lg:-tracking-button text-base leading-5 font-medium lg:text-xl lg:leading-6 lg:font-bold">
              Linked Injury/Illness
            </span>
            <div className="flex items-center gap-15">
              <p className="text-neutral-tones-text -tracking-form-input lg:-tracking-text-area text-base leading-5 font-bold capitalize lg:text-lg lg:leading-5">
                {data?.incident?.title}
              </p>
              <span className="border-l-neutral-tones-text/10 hidden h-5 border-l md:block"></span>
              <p className="font-inter-variable text-neutral-tones-text/80 -tracking-form-label lg:-tracking-form-input hidden text-sm leading-5 font-normal whitespace-nowrap md:block lg:text-base">
                Reference ID{' '}
                <span className="text-neutral-tones-text/50 uppercase">
                  {data?.incident?.incidentRefId}
                </span>
              </p>
            </div>
          </div>
          <Link target="_blank" to={`/incident-review/${data?.incident?.id}`}>
            <ChevronLeftIcon className="[&>path]:fill-neutral-tones-text h-6 w-6 -rotate-90 md:h-12 md:w-12" />
          </Link>
        </div>
      </div>
      <h4>Reported Details</h4>
      <div className="mt-9 flex flex-wrap gap-9">
        <div className="flex flex-col gap-5">
          <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
            Injury or Illness
            <span className="text-brand-posh-pink">*</span>
          </h6>
          <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
            {data?.type}
          </p>
        </div>
        <div className="flex flex-col gap-5">
          <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
            Type of Loss
            <span className="text-brand-posh-pink">*</span>
          </h6>
          <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
            {data?.typeOfLoss}
          </p>
        </div>
        <div className="flex flex-col gap-5">
          <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
            Classification
            <span className="text-brand-posh-pink">*</span>
          </h6>
          <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
            {data?.classification}
          </p>
        </div>
        {data?.classifyIllness && (
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Classify Illness
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              {data?.classifyIllness}
            </p>
          </div>
        )}
        {data?.daysAwayFromWork && (
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Days Away From Work
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              {data?.daysAwayFromWork}
            </p>
          </div>
        )}
        {data?.jobRestrictionDays && (
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Job Restriction Days
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              {data?.jobRestrictionDays}
            </p>
          </div>
        )}
        {data?.dateOfDeath && (
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Date Of Death
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              {moment(data?.dateOfDeath).format('MMM DD YYYY')}
            </p>
          </div>
        )}
      </div>
      <div className="mt-9 flex flex-col">
        <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
          Body Parts Affected
        </h6>
        <p className="font-inter-variable text-neutral-tones-text mt-1 text-base leading-5 font-normal">
          Body parts of the employee affected by the injury/illness due to the
          incident:
        </p>

        <div className="relative mt-5 flex max-w-545 flex-wrap gap-2.5">
          {Object?.values(InvestigationAffectedBodyPartsEnum)?.map(
            (node, index) => (
              <Button
                variant="primary"
                size="smaller"
                type="button"
                key={index}
                disable={!data?.affectedBodyParts?.includes(node)}
                otherClasses={clsx(
                  'w-121 hover:!bg-brand-byzantine/80 hover:!text-white disabled:!bg-additional-disable disabled:!text-neutral-tones-text/50',
                )}
              >
                {node}
              </Button>
            ),
          )}
        </div>
      </div>
      {(data?.fiveWhys || [])?.length > 0 && (
        <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
          <button
            type="button"
            onClick={() => setToggle(toggle === 'step-2' ? '' : 'step-2')}
            className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
          >
            5 WHY’s
            <ChevronIcon
              className={clsx(
                'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
                toggle === 'step-2' && 'rotate-180',
              )}
            />
          </button>
          {toggle === 'step-2' && (
            <div
              className={clsx(
                'mt-6 grid h-auto max-w-766 grid-cols-1 gap-x-6 gap-y-12 overflow-hidden transition-all duration-300 sm:grid-cols-2',
              )}
            >
              {data?.fiveWhys?.map((node: any, index: number) => (
                <div key={index} className="flex flex-col gap-15">
                  <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal lg:text-lg lg:leading-5">
                    {node?.question}
                  </h6>
                  <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                    {node?.answer || 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {data?.attachments?.length === 0 &&
        (data?.customQuestions || [])?.length > 0 && (
          <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
            <button
              type="button"
              onClick={() => setToggle(toggle === 'step-3' ? '' : 'step-3')}
              className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
            >
              CUSTOM QUESTIONS
              <ChevronIcon
                className={clsx(
                  'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
                  toggle === 'step-3' && 'rotate-180',
                )}
              />
            </button>
            {toggle === 'step-3' && (
              <div
                className={clsx(
                  'mt-6 grid h-auto max-w-766 grid-cols-1 gap-4 overflow-hidden transition-all duration-300 sm:grid-cols-2 sm:gap-6',
                )}
              >
                {data?.customQuestions?.map((node, index) => (
                  <div
                    key={index}
                    className="rounded-10 border-neutral-tones-text/10 bg-translucent-background border p-4"
                  >
                    <div className="w-full">
                      <p className="font-inter-variable text-neutral-tones-text mb-2 w-full text-base/5 font-bold">
                        Question:{' '}
                        <span className="font-normal">
                          {node?.investigationQuestion?.question}
                        </span>
                      </p>
                      <p className="font-inter-variable text-neutral-tones-text mb-2 w-full text-base/5 font-bold">
                        Type:{' '}
                        <span className="font-normal">
                          {node?.investigationQuestion?.type}
                        </span>
                      </p>
                      <p className="font-inter-variable text-neutral-tones-text mb-2 w-full text-base/5 font-bold">
                        Question Category:{' '}
                        <span className="font-normal">
                          {
                            node?.investigationQuestion
                              ?.investigationQuestionCategory?.name
                          }
                        </span>
                      </p>
                      {node?.investigationQuestion?.type ===
                        InvestigationQuestionTypeEnum?.MultiOptions && (
                        <p className="font-inter-variable text-neutral-tones-text mb-2 w-full text-base/5 font-bold">
                          Options:{' '}
                          <span className="font-normal">
                            {node?.investigationQuestion?.options?.join(', ')}
                          </span>
                        </p>
                      )}
                      <p className="text-neutral-tones-text w-full text-base font-bold">
                        Answer:{' '}
                        <span className="font-normal">{node?.answer}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      {data?.attachments?.length === 0 &&
        (data?.otherQuestionAnswer || [])?.length > 0 && (
          <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
            <button
              type="button"
              onClick={() =>
                setToggle(toggle === 'other-question' ? '' : 'other-question')
              }
              className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
            >
              OTHER QUESTIONS
              <ChevronIcon
                className={clsx(
                  'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
                  toggle === 'other-question' && 'rotate-180',
                )}
              />
            </button>
            {toggle === 'other-question' && (
              <div
                className={clsx(
                  'mt-6 grid h-auto max-w-955 grid-cols-1 gap-x-6 gap-y-12 overflow-hidden transition-all duration-300 sm:grid-cols-2',
                )}
              >
                {data?.otherQuestionAnswer?.map((node, index: number) => (
                  <div key={index} className="flex flex-col gap-15">
                    <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                      {node?.question}
                    </h6>
                    <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                      {node?.answer || 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
        <button
          type="button"
          onClick={() =>
            setToggle(toggle === 'other-context' ? '' : 'other-context')
          }
          className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
        >
          Incident Narative
          <ChevronIcon
            className={clsx(
              'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
              toggle === 'other-context' && 'rotate-180',
            )}
          />
        </button>
        {toggle === 'other-context' && (
          <div
            className={clsx(
              'mt-6 grid h-auto max-w-955 grid-cols-1 gap-x-6 gap-y-12 overflow-hidden transition-all duration-300 sm:grid-cols-2',
            )}
          >
            <div className="flex flex-col gap-15">
              <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                What was employee doing just before the incident occurred?
              </h6>
              <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                {data?.narBeforeIncident || 'N/A'}
              </p>
            </div>
            <div className="flex flex-col gap-15">
              <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                What happened? Tell us how the injury or illness occurred?
              </h6>
              <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                {data?.narWhatHappened || 'N/A'}
              </p>
            </div>
            <div className="flex flex-col gap-15">
              <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                What object or substance directly harmed the employee?
              </h6>
              <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                {data?.narObjectSubstance || 'N/A'}
              </p>
            </div>

            {(data?.classification ===
              InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum.Hospitalized ||
              data?.typeOfLoss ===
                InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.MedicalTreatmentBeyondFirstAid) &&
              data?.treatmentFacilityType && (
                <div className="flex flex-col gap-15">
                  <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                    Was employee treated in emergency room?
                  </h6>
                  <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                    {data?.treatmentFacilityType || 'N/A'}
                  </p>
                </div>
              )}
          </div>
        )}
      </div>
      {Array.isArray(data?.attachments) && data.attachments.length > 0 && (
        <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
          <button
            type="button"
            className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
          >
            CUSTOM INVESTIGATION UPLOADED
          </button>
          <div className="mt-6 flex flex-col gap-2.5">
            {data?.attachments?.map((file) => {
              const isPreviewable =
                file?.mediaType?.startsWith('image/') ||
                file?.mediaType?.startsWith('video/') ||
                file?.mediaType === 'application/pdf' ||
                file?.mediaType ===
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                file?.mediaType === 'application/msword';
              return (
                <Fragment key={file?.id}>
                  {!isPreviewable ? (
                    <Link
                      to={file?.url || ''}
                      className="border-brand-fair-orange hover:text-brand-byzantine hover:border-brand-byzantine text-neutral-tones-text font-inter-variable flex cursor-pointer items-center gap-2.5 rounded-md border p-15 text-sm leading-4 font-bold transition-all duration-300"
                      download
                      target="_blank"
                    >
                      <DownloadIcon className="h-5 w-5 [&>g>path]:fill-current" />{' '}
                      {file?.name}
                    </Link>
                  ) : (
                    <button
                      onClick={() => {
                        setSelectedAttachment(file);
                        dispatch(toggleModal('attachment-preview-modal'));
                      }}
                      className="border-brand-fair-orange hover:text-brand-byzantine hover:border-brand-byzantine text-neutral-tones-text font-inter-variable flex cursor-pointer items-center gap-2.5 rounded-md border p-15 text-sm leading-4 font-bold transition-all duration-300 text-left"
                    >
                      <DownloadIcon className="h-5 w-5 [&>g>path]:fill-current" />{' '}
                      {file?.name}
                    </button>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
      <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
        <button
          type="button"
          onClick={() => setToggle(toggle === 'step-4' ? '' : 'step-4')}
          className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
        >
          ROOT CAUSE ANALYSIS
          <ChevronIcon
            className={clsx(
              'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
              toggle === 'step-4' && 'rotate-180',
            )}
          />
        </button>
        {toggle === 'step-4' && (
          <div
            className={clsx(
              'mt-5 h-auto max-w-955 gap-9 overflow-hidden transition-all duration-300',
            )}
          >
            <p className="text-neutral-tones-text font-inter-variable text-lg leading-5 font-normal capitalize">
              {data?.rootCauseAnalysis}
            </p>
          </div>
        )}
      </div>{' '}
      {canEdit && (
        <div className="mt-9">
          <Button
            size={buttonSize}
            href={`/injuries/create-investigation/${data?.incidentId}?investigationId=${data?.id}`}
            otherClasses="w-fit"
          >
            <EditIcon className="h-6 w-6 [&>path]:fill-current" />
            Edit Details
          </Button>
        </div>
      )}
      {(data?.taskHistory || [])?.length > 0 && (
        <ChangesHistory data={data?.taskHistory} />
      )}

      {modalName === 'attachment-preview-modal' && (
        <ModalComponent
          otherClasses="px-4 sm:px-0"
          additionalClass="w-full max-w-4xl rounded-2xl p-5 md:py-6 md:px-9"
        >
          <AttachmentPreviewModal
            handleClose={() => {
              dispatch(toggleModal(''));
              setSelectedAttachment(null);
            }}
            attachment={selectedAttachment}
          />
        </ModalComponent>
      )}
    </div>
  );
}
